import { ensureAccessSession, installSessionRecovery } from './sessionProtocol';
import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL_WITHOUT_PATH } from '../../../config/api';
import {
  resolveKeepAliveRequestPanePath,
  shouldAllowKeepAliveHttpRequest,
} from '../routing/keepAliveNetworkGate';
import {
  createKeepAliveInactivePaneError,
  isKeepAliveInactivePaneError,
} from '../routing/keepAliveErrors';
import { attachRateLimitInterceptor } from './rateLimit';

export interface KeepAliveAxiosRequestConfig extends AxiosRequestConfig {
  keepAlivePanePath?: string;
  /**
   * `Authorization` já resolvido por quem chamou (dedupe em voo, `utils/httpGetDedupe`):
   * o interceptor não relê o localStorage, então a credencial enviada é exatamente a da
   * chave de dedupe.
   */
  authorizationPinned?: boolean;
}

class HttpService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL_WITHOUT_PATH,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(async (config) => {
      const panePath = resolveKeepAliveRequestPanePath(
        (config as KeepAliveAxiosRequestConfig).keepAlivePanePath,
      );

      if (!shouldAllowKeepAliveHttpRequest(panePath)) {
        return Promise.reject(createKeepAliveInactivePaneError());
      }

      if (!(config as KeepAliveAxiosRequestConfig).authorizationPinned) {
        const token = await ensureAccessSession();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      if (config.data instanceof FormData) {
        if (config.headers instanceof AxiosHeaders) {
          config.headers.delete('Content-Type');
        } else {
          delete (config.headers as Record<string, unknown>)['Content-Type'];
        }
      }

      return config;
    });

    // 429 é interpretado (o erro ganha `rateLimit`), nunca repetido aqui.
    // Registrado ANTES do interceptor de erro para que o decorador rode primeiro:
    // os interceptors de resposta do Axios executam na ordem em que são
    // adicionados, então quem tratar o erro depois já enxerga `error.rateLimit`.
    attachRateLimitInterceptor(this.api);
    installSessionRecovery(this.api);

    // Interceptor para tratar erros
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (isKeepAliveInactivePaneError(error)) {
          return Promise.reject(error);
        }

        // Session lifecycle belongs exclusively to installSessionRecovery.

        
        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }
}

export const httpService = new HttpService();
